import React, { useEffect, useState } from "react";
import { Box, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import axios from "axios";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import {
  useEngagementTheme,
  engagementPageSx,
  EngagementPill,
  engagementChipSx,
  engagementSelectSx,
} from "../components/engagementTheme";

const STATUS_TONE = { active: "ok", new: "info", inactive: "crit" };

const renewTone = (daysLeft) => {
  if (daysLeft === null || daysLeft === undefined) return "mute";
  if (daysLeft < 0) return "crit";
  if (daysLeft <= 90) return "warn";
  return "ok";
};

const renewText = (daysLeft) => {
  if (daysLeft === null || daysLeft === undefined) return "No active subscription";
  if (daysLeft < 0) return `Ended ${Math.abs(daysLeft)} days ago`;
  if (daysLeft === 0) return "Ends today";
  return `In ${daysLeft} days`;
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  const datePart = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  const timePart = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart}, ${timePart}`;
};

const RENEWAL_WINDOWS = [
  { value: "any", label: "Any time" },
  { value: "30", label: "Within 30 days" },
  { value: "90", label: "Within 90 days" },
  { value: "ended", label: "Ended, not renewed" },
];

const TERM_LABEL = { monthly: "Monthly", yearly: "Yearly" };
const INSTALLMENT_LABEL = { monthly: "monthly", quarterly: "quarterly", "bi-annual": "bi-annually" };
const STATUS_LABEL = { active: "Active", new: "New installs", inactive: "Inactive" };
const ACTIVATED_LABEL = { 30: "Last 30 days", 90: "Last 90 days", 365: "Last year" };

const EngagementClinics = () => {
  const [rows, setRows] = useState([]);
  const [planOptions, setPlanOptions] = useState([]);
  const [officers, setOfficers] = useState([]);
  const [renewingWithin90, setRenewingWithin90] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(15);
  const [totalCount, setTotalCount] = useState(0);
  const [allClinicsCount, setAllClinicsCount] = useState(0);
  const [search, setSearch] = useState("");
  const [plan, setPlan] = useState("all");
  const [renewal, setRenewal] = useState("any");
  const [owner, setOwner] = useState("all");
  const [quick, setQuick] = useState("all");
  const [status, setStatus] = useState("all");
  const [term, setTerm] = useState("all");
  const [city, setCity] = useState("all");
  const [state, setState] = useState("all");
  const [activated, setActivated] = useState("any");
  const [onboarding, setOnboarding] = useState("any");
  const [renewed, setRenewed] = useState("any");
  const [statusOptions, setStatusOptions] = useState([]);
  const [termOptions, setTermOptions] = useState([]);
  const [activatedOptions, setActivatedOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [noCallScheduledCount, setNoCallScheduledCount] = useState(0);
  const [multipleOpenTasksCount, setMultipleOpenTasksCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [schedulingId, setSchedulingId] = useState("");
  const [scheduleValue, setScheduleValue] = useState("");
  const Toast = useToast();
  const auth = localStorage.getItem("token");
  const t = useEngagementTheme();

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/officers`, {
        headers: { Authorization: `Bearer ${auth}` },
      })
      .then((res) => {
        if (!cancelled) setOfficers(res.data.officers || []);
      })
      .catch(() => {
        // Owner filter just stays at "Everyone" if this fails — not fatal to the page.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    const timeout = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/clinics`,
          {
            params: { page, limit: rowsPerPage, q: search || undefined, plan, renewal, owner, quick, status, term, city, state, activated, onboarding, renewed },
            headers: { Authorization: `Bearer ${auth}` },
          }
        );
        if (cancelled) return;
        setRows(res.data.rows || []);
        setTotalCount(res.data.filteredItemsCount || 0);
        setAllClinicsCount(res.data.totalItems || 0);
        setPlanOptions(res.data.planOptions || []);
        setStatusOptions(res.data.statusOptions || []);
        setTermOptions(res.data.termOptions || []);
        setActivatedOptions(res.data.activatedOptions || []);
        setCityOptions(res.data.cityOptions || []);
        setStateOptions(res.data.stateOptions || []);
        setRenewingWithin90(res.data.summary?.renewingWithin90 || 0);
        setNoCallScheduledCount(res.data.summary?.noCallScheduledCount || 0);
        setMultipleOpenTasksCount(res.data.summary?.multipleOpenTasksCount || 0);
      } catch (error) {
        if (!cancelled) Toast.error("Error while fetching clinics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, search ? 400 : 0);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, plan, renewal, owner, quick, status, term, city, state, activated, onboarding, renewed]);

  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  const saveScheduledCall = async (doctorId) => {
    if (!scheduleValue) return;
    try {
      const iso = new Date(scheduleValue).toISOString();
      await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/clinics/${doctorId}/next-call`,
        { nextCallAt: iso },
        { headers: { Authorization: `Bearer ${auth}` } }
      );
      setRows((prev) => prev.map((r) => (r.id === doctorId ? { ...r, nextCallAt: iso } : r)));
      setSchedulingId("");
      setScheduleValue("");
      Toast.success("Call scheduled.");
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not schedule the call.");
    }
  };

  const quickFilters = [
    { value: "all", label: "All", enabled: true },
    { value: "no_call_scheduled", label: `No call scheduled (${noCallScheduledCount})`, enabled: true },
    { value: "multiple_open_tasks", label: `More than one open task (${multipleOpenTasksCount})`, enabled: true },
  ];

  const selectSx = engagementSelectSx(t);

  return (
    <Box sx={engagementPageSx(t)}>
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-end", gap: "16px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.015em", color: t.textPrimary }}>Clinics</Typography>
          <Typography sx={{ fontSize: 15, color: t.textMuted }}>
            Showing {totalCount} of {allClinicsCount} clinics. {renewingWithin90} renewing within 90 days.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography component="label" htmlFor="find" sx={{ fontSize: 13, color: t.textMuted }}>Search a doctor</Typography>
          <TextField
            id="find"
            size="small"
            placeholder="Doctor, clinic or phone"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            sx={{ width: 280, maxWidth: "100%", bgcolor: t.inputBg, "& .MuiOutlinedInput-root": { height: 44 } }}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "flex-end" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Owner</Typography>
          <Select
            size="small"
            value={owner}
            onChange={(e) => {
              setPage(1);
              setOwner(e.target.value);
            }}
            sx={selectSx}
          >
            <MenuItem value="all">Everyone</MenuItem>
            {officers.map((o) => (
              <MenuItem key={o._id} value={o._id}>{o.name}</MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Plan</Typography>
          <Select
            size="small"
            value={plan}
            onChange={(e) => {
              setPage(1);
              setPlan(e.target.value);
            }}
            sx={selectSx}
          >
            <MenuItem value="all">All plans</MenuItem>
            {planOptions.map((p) => (
              <MenuItem key={p} value={p}>{p}</MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Renewal</Typography>
          <Select
            size="small"
            value={renewal}
            onChange={(e) => {
              setPage(1);
              setRenewal(e.target.value);
            }}
            sx={selectSx}
          >
            {RENEWAL_WINDOWS.map((r) => (
              <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Status</Typography>
          <Select size="small" value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} sx={selectSx}>
            <MenuItem value="all">All</MenuItem>
            {statusOptions.map((s) => (
              <MenuItem key={s} value={s}>{STATUS_LABEL[s] || s}</MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Term</Typography>
          <Select size="small" value={term} onChange={(e) => { setPage(1); setTerm(e.target.value); }} sx={selectSx}>
            <MenuItem value="all">All terms</MenuItem>
            {termOptions.map((termOpt) => (
              <MenuItem key={termOpt} value={termOpt}>{TERM_LABEL[termOpt] || termOpt}</MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>City</Typography>
          <Select size="small" value={city} onChange={(e) => { setPage(1); setCity(e.target.value); }} sx={selectSx}>
            <MenuItem value="all">All cities</MenuItem>
            {cityOptions.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>State</Typography>
          <Select size="small" value={state} onChange={(e) => { setPage(1); setState(e.target.value); }} sx={selectSx}>
            <MenuItem value="all">All states</MenuItem>
            {stateOptions.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Activated</Typography>
          <Select size="small" value={activated} onChange={(e) => { setPage(1); setActivated(e.target.value); }} sx={selectSx}>
            <MenuItem value="any">Any time</MenuItem>
            {activatedOptions.map((a) => (
              <MenuItem key={a} value={a}>{ACTIVATED_LABEL[a] || a}</MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Onboarding</Typography>
          <Select size="small" value={onboarding} onChange={(e) => { setPage(1); setOnboarding(e.target.value); }} sx={selectSx}>
            <MenuItem value="any">Any</MenuItem>
            <MenuItem value="done">Done</MenuItem>
            <MenuItem value="not_done">Not done</MenuItem>
          </Select>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Has renewed</Typography>
          <Select size="small" value={renewed} onChange={(e) => { setPage(1); setRenewed(e.target.value); }} sx={selectSx}>
            <MenuItem value="any">Any</MenuItem>
            <MenuItem value="yes">Renewed</MenuItem>
            <MenuItem value="no">Not renewed</MenuItem>
          </Select>
        </Box>
        {(plan !== "all" || renewal !== "any" || owner !== "all" || status !== "all" || term !== "all" || city !== "all" || state !== "all" || activated !== "any" || onboarding !== "any" || renewed !== "any" || quick !== "all" || search) && (
          <Box
            component="button"
            type="button"
            onClick={() => {
              setPage(1);
              setPlan("all");
              setRenewal("any");
              setOwner("all");
              setStatus("all");
              setTerm("all");
              setCity("all");
              setState("all");
              setActivated("any");
              setOnboarding("any");
              setRenewed("any");
              setQuick("all");
              setSearch("");
            }}
            sx={{ minHeight: 44, padding: "0 14px", border: 0, bgcolor: "transparent", color: t.textSecondary, font: "inherit", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
          >
            Clear filters
          </Box>
        )}
        <Box role="group" aria-label="Quick filters" sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {quickFilters.map((f) => (
            <Box
              key={f.value}
              component="button"
              type="button"
              onClick={() => {
                setPage(1);
                setQuick(f.value);
              }}
              sx={engagementChipSx(t, quick === f.value)}
            >
              {f.label}
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ position: "relative", overflowX: "auto", bgcolor: t.cardBg, border: `1px solid ${t.border}`, borderRadius: "14px", display: { xs: "none", md: "block" } }}>
        {loading && <PageLoader />}
        <Box component="table" sx={{ width: "100%", minWidth: 1480, borderCollapse: "collapse", fontSize: 14 }}>
          <Box component="thead">
            <Box component="tr" sx={{ textAlign: "left", color: t.textMuted, fontSize: 13 }}>
              {["Doctor and clinic", "City, state", "Status", "Plan and term", "Activated", "Subscription valid till", "Next call", "Open tasks", "Owner"].map((h, i, arr) => (
                <Box
                  component="th"
                  key={h}
                  sx={{
                    padding: i === 0 || i === arr.length - 1 ? "12px 16px" : "12px",
                    borderBottom: `1px solid ${t.border}`,
                    fontWeight: 600,
                  }}
                >
                  {h}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {rows.map((r) => (
              <Box component="tr" key={r.id} sx={{ verticalAlign: "top" }}>
                <Box component="td" sx={{ padding: "14px 16px", borderBottom: `1px solid ${t.borderMuted}` }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <Typography component={Link} to={`/dashboard/engagement/clinics/${r.id}`} sx={{ fontSize: 15, fontWeight: 600, textDecoration: "none", color: t.accent }}>{r.doctorName}</Typography>
                    <Typography sx={{ fontSize: 14, color: t.textSecondary }}>{r.clinicName || "—"}</Typography>
                    {r.phone && (
                      <Typography component="a" href={`tel:${r.phone}`} sx={{ fontSize: 14, fontWeight: 600, textDecoration: "none", color: t.accent }}>{r.phone}</Typography>
                    )}
                  </Box>
                </Box>
                <Box component="td" sx={{ padding: "14px 12px", borderBottom: `1px solid ${t.borderMuted}` }}>
                  <Typography sx={{ fontSize: 14 }}>{r.city || "—"}</Typography>
                  <Typography sx={{ fontSize: 13, color: t.textMuted }}>{r.state || "—"}</Typography>
                </Box>
                <Box component="td" sx={{ padding: "14px 12px", borderBottom: `1px solid ${t.borderMuted}` }}>
                  <EngagementPill t={t} tone={STATUS_TONE[r.statusGroup] || "mute"}>{STATUS_LABEL[r.statusGroup] || r.statusGroup}</EngagementPill>
                </Box>
                <Box component="td" sx={{ padding: "14px 12px", borderBottom: `1px solid ${t.borderMuted}` }}>
                  <Typography sx={{ fontWeight: 600 }}>{r.plan || "—"}</Typography>
                  {r.term && (
                    <Typography sx={{ fontSize: 13, color: t.textMuted }}>
                      {TERM_LABEL[r.term] || r.term}{r.installmentType ? `, billed ${INSTALLMENT_LABEL[r.installmentType] || r.installmentType}` : ""}
                    </Typography>
                  )}
                </Box>
                <Box component="td" sx={{ padding: "14px 12px", borderBottom: `1px solid ${t.borderMuted}` }}>
                  <Typography sx={{ fontSize: 14 }}>{formatDate(r.activatedAt)}</Typography>
                </Box>
                <Box component="td" sx={{ padding: "14px 12px", borderBottom: `1px solid ${t.borderMuted}` }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <Typography sx={{ fontWeight: 600, color: t.txtTone[renewTone(r.daysLeft)] }}>{formatDate(r.subscriptionEnd)}</Typography>
                    <Typography sx={{ fontSize: 13, color: t.textMuted }}>{renewText(r.daysLeft)}</Typography>
                  </Box>
                </Box>
                <Box component="td" sx={{ padding: "14px 12px", borderBottom: `1px solid ${t.borderMuted}` }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-start" }}>
                    <Typography sx={{ fontWeight: 600, color: r.nextCallAt ? t.txtTone.ok : t.txtTone.mute }}>
                      {r.nextCallAt ? formatDateTime(r.nextCallAt) : "Not scheduled"}
                    </Typography>
                    {!r.nextCallAt && schedulingId === r.id && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
                        <TextField
                          size="small"
                          type="datetime-local"
                          InputLabelProps={{ shrink: true }}
                          value={scheduleValue}
                          onChange={(e) => setScheduleValue(e.target.value)}
                          sx={{ bgcolor: t.inputBg, width: 190 }}
                        />
                        <Box component="button" type="button" disabled={!scheduleValue} onClick={() => saveScheduledCall(r.id)} sx={{ minHeight: 36, padding: "0 10px", border: 0, borderRadius: "8px", bgcolor: t.accent, color: t.accentSolidText, font: "inherit", fontSize: 13, fontWeight: 600, cursor: scheduleValue ? "pointer" : "not-allowed", opacity: scheduleValue ? 1 : 0.6 }}>
                          Save
                        </Box>
                        <Box component="button" type="button" onClick={() => { setSchedulingId(""); setScheduleValue(""); }} sx={{ minHeight: 36, padding: "0 10px", border: 0, bgcolor: "transparent", color: t.textSecondary, font: "inherit", fontSize: 13, cursor: "pointer" }}>
                          Cancel
                        </Box>
                      </Box>
                    )}
                    {!r.nextCallAt && schedulingId !== r.id && (
                      r.hasOwner ? (
                        <Box
                          component="button"
                          type="button"
                          onClick={() => setSchedulingId(r.id)}
                          sx={{
                            minHeight: 44,
                            padding: "0 12px",
                            border: `1px solid ${t.accent}`,
                            borderRadius: "8px",
                            bgcolor: t.cardBg,
                            color: t.accent,
                            font: "inherit",
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          Schedule call
                        </Box>
                      ) : (
                        <Tooltip title="This clinic has no engagement owner yet">
                          <span>
                            <Box
                              component="button"
                              type="button"
                              disabled
                              sx={{
                                minHeight: 44,
                                padding: "0 12px",
                                border: `1px solid ${t.disabledBorder}`,
                                borderRadius: "8px",
                                bgcolor: t.cardBg,
                                color: t.disabledText,
                                font: "inherit",
                                fontSize: 13,
                                fontWeight: 600,
                                cursor: "not-allowed",
                              }}
                            >
                              Schedule call
                            </Box>
                          </span>
                        </Tooltip>
                      )
                    )}
                  </Box>
                </Box>
                <Box component="td" sx={{ padding: "14px 12px", borderBottom: `1px solid ${t.borderMuted}` }}>
                  <EngagementPill t={t} tone={r.openTasksCount > 1 ? "warn" : r.openTasksCount === 1 ? "info" : "mute"}>
                    {r.openTasksCount > 0 ? `${r.openTasksCount} open` : "None"}
                  </EngagementPill>
                </Box>
                <Box component="td" sx={{ padding: "14px 16px", borderBottom: `1px solid ${t.borderMuted}`, fontSize: 13, color: t.textSecondary }}>
                  {r.ownerName || "Unassigned"}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
        {rows.length === 0 && !loading && (
          <Typography sx={{ margin: 0, padding: "18px 16px", fontSize: 14, color: t.textMuted }}>No clinics match these filters.</Typography>
        )}
      </Box>

      <Box sx={{ position: "relative", display: { xs: "flex", md: "none" }, flexDirection: "column", gap: "12px" }}>
        {loading && <PageLoader />}
        {rows.map((r) => (
          <Box key={r.id} sx={{ bgcolor: t.cardBg, border: `1px solid ${t.border}`, borderRadius: "12px", padding: "16px", display: "flex", flexDirection: "column", gap: "10px" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px" }}>
                <Typography component={Link} to={`/dashboard/engagement/clinics/${r.id}`} sx={{ fontSize: 16, fontWeight: 600, textDecoration: "none", color: t.accent }}>{r.doctorName}</Typography>
                <EngagementPill t={t} tone={STATUS_TONE[r.statusGroup] || "mute"}>{STATUS_LABEL[r.statusGroup] || r.statusGroup}</EngagementPill>
              </Box>
              <Typography sx={{ fontSize: 14, color: t.textSecondary }}>{r.clinicName || "—"}{[r.city, r.state].filter(Boolean).length ? `, ${[r.city, r.state].filter(Boolean).join(", ")}` : ""}</Typography>
              {r.phone && (
                <Typography component="a" href={`tel:${r.phone}`} sx={{ fontSize: 14, fontWeight: 600, textDecoration: "none", color: t.accent }}>{r.phone}</Typography>
              )}
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 12px" }}>
              <Box>
                <Typography sx={{ fontSize: 12, color: t.textMuted }}>Plan and term</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  {r.plan || "—"}{r.term ? `, ${TERM_LABEL[r.term] || r.term}` : ""}{r.installmentType ? `, billed ${INSTALLMENT_LABEL[r.installmentType] || r.installmentType}` : ""}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 12, color: t.textMuted }}>Activated</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{formatDate(r.activatedAt)}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 12, color: t.textMuted }}>Subscription valid till</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: t.txtTone[renewTone(r.daysLeft)] }}>{formatDate(r.subscriptionEnd)}</Typography>
                <Typography sx={{ fontSize: 12, color: t.textMuted }}>{renewText(r.daysLeft)}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 12, color: t.textMuted }}>Next call</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: r.nextCallAt ? t.txtTone.ok : t.txtTone.mute }}>
                  {r.nextCallAt ? formatDateTime(r.nextCallAt) : "Not scheduled"}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px" }}>
              <EngagementPill t={t} tone={r.openTasksCount > 1 ? "warn" : r.openTasksCount === 1 ? "info" : "mute"}>
                {r.openTasksCount > 0 ? `${r.openTasksCount} open task${r.openTasksCount === 1 ? "" : "s"}` : "No open tasks"}
              </EngagementPill>
              <Typography sx={{ fontSize: 13, color: t.textSecondary }}>Owner: {r.ownerName || "Unassigned"}</Typography>
            </Box>

            {!r.nextCallAt && schedulingId === r.id && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
                <TextField
                  size="small"
                  type="datetime-local"
                  InputLabelProps={{ shrink: true }}
                  value={scheduleValue}
                  onChange={(e) => setScheduleValue(e.target.value)}
                  sx={{ bgcolor: t.inputBg, width: 190 }}
                />
                <Box component="button" type="button" disabled={!scheduleValue} onClick={() => saveScheduledCall(r.id)} sx={{ minHeight: 36, padding: "0 10px", border: 0, borderRadius: "8px", bgcolor: t.accent, color: t.accentSolidText, font: "inherit", fontSize: 13, fontWeight: 600, cursor: scheduleValue ? "pointer" : "not-allowed", opacity: scheduleValue ? 1 : 0.6 }}>
                  Save
                </Box>
                <Box component="button" type="button" onClick={() => { setSchedulingId(""); setScheduleValue(""); }} sx={{ minHeight: 36, padding: "0 10px", border: 0, bgcolor: "transparent", color: t.textSecondary, font: "inherit", fontSize: 13, cursor: "pointer" }}>
                  Cancel
                </Box>
              </Box>
            )}
            {!r.nextCallAt && schedulingId !== r.id && (
              r.hasOwner ? (
                <Box
                  component="button"
                  type="button"
                  onClick={() => setSchedulingId(r.id)}
                  sx={{ minHeight: 44, padding: "0 12px", border: `1px solid ${t.accent}`, borderRadius: "8px", bgcolor: t.cardBg, color: t.accent, font: "inherit", fontSize: 13, fontWeight: 600, cursor: "pointer", alignSelf: "flex-start" }}
                >
                  Schedule call
                </Box>
              ) : (
                <Tooltip title="This clinic has no engagement owner yet">
                  <span>
                    <Box component="button" type="button" disabled sx={{ minHeight: 44, padding: "0 12px", border: `1px solid ${t.disabledBorder}`, borderRadius: "8px", bgcolor: t.cardBg, color: t.disabledText, font: "inherit", fontSize: 13, fontWeight: 600, cursor: "not-allowed" }}>
                      Schedule call
                    </Box>
                  </span>
                </Tooltip>
              )
            )}
          </Box>
        ))}
        {rows.length === 0 && !loading && (
          <Typography sx={{ fontSize: 14, color: t.textMuted }}>No clinics match these filters.</Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
        <Typography sx={{ fontSize: 13, color: t.textMuted }}>Page {page} of {totalPages}</Typography>
        <Box sx={{ display: "flex", gap: "8px" }}>
          <Box
            component="button"
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: page <= 1 ? "not-allowed" : "pointer" }}
          >
            Previous
          </Box>
          <Box
            component="button"
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: page >= totalPages ? "not-allowed" : "pointer" }}
          >
            Next
          </Box>
        </Box>
      </Box>

      <Typography sx={{ fontSize: 13, color: t.textMuted }}>
        Plan, activation and subscription come from Orders. Everything else is edited on the clinic page and saved to
        the master database.
      </Typography>
    </Box>
  );
};

export default EngagementClinics;
